<template>
  <div class="mine-page">
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <van-loading type="spinner" color="#1677ff" size="24px" />
      <p class="loading-text">加载中...</p>
    </div>

    <!-- 页面内容 -->
    <div v-else class="page-content">
      <!-- 个人信息卡片 -->
      <div class="profile-card">
        <div class="profile-header">
          <div class="avatar">
            <img :src="userInfo.user?.avatar || 'https://bes-education.oss-cn-shenzhen.aliyuncs.com/2024/11/05/3ab462800e674220a25dadbdc55879c5.jpg'" alt="头像" />
          </div>
          <div class="profile-info">
            <div class="name-row">
              <div class="name">{{ userInfo.user?.nickName || '老师' }}</div>
              <CompanySwitcher :company-ids="userInfo.companyIds || []" />
            </div>
            <div class="profile-meta">
              <span class="campus">{{ userInfo.user?.dept?.deptName || '-' }}</span>
              <!-- <span class="role">{{ (userInfo.user?.roles || []).map(r => r.roleName).join('、') || '-' }}</span> -->
            </div>
          </div>
        </div>
      </div>

      <!-- 数据统计 -->
      <div class="stats-section" v-if="hasRole('teacher')">
        <div class="stat-card" @click="goToComposition(1)">
          <div class="stat-value">{{ classHourStatics.totalClassHourCount || 0 }}</div>
          <div class="stat-label">累计课时</div>
        </div>
        <div class="stat-card" @click="goToComposition(2)">
          <div class="stat-value">{{ classHourStatics.monthClassHourCount || 0 }}</div>
          <div class="stat-label">本月课时</div>
        </div>
        <div class="stat-card" @click="goToComposition(3)">
          <div class="stat-value">{{ classHourStatics.weekClassHourCount || 0 }}</div>
          <div class="stat-label">本周课时</div>
        </div>
      </div>

      <!-- 设置列表 -->
      <div class="settings-section">
        <van-cell-group inset>
          <van-cell 
            v-for="(item, idx) in settingsItems" 
            :key="idx"
            :title="item.title"
            :icon="item.icon"
            is-link
            @click="handleSettingClick(item)"
          />
        </van-cell-group>
      </div>
    </div>
  </div>
</template>

<script setup>
defineOptions({ name: 'Mine' })

import { getTeacherClassHourStatics } from '@/api/schedule'

import { onMounted, ref, computed } from 'vue'
import { onActivated } from 'vue'
import { getUserInfo } from '@/api/auth'
import { setUserInfo } from '@/utils/authUtils'
import { ensureWecomLogin } from '@/utils/wecomAuth'
import { useRoute } from 'vue-router'
import { useRouter } from 'vue-router'
import { initializeCompanyContext } from '@/utils/company'
import CompanySwitcher from '@/components/CompanySwitcher.vue'


const route = useRoute()
const router = useRouter()
const userInfo = ref({})
const loading = ref(true)
const classHourStatics = ref({})

function hasRole(role) {
  const roles = userInfo.value?.user?.roles || []
  return roles.some(r => r.roleName === role || r.roleKey === role)
}

const settingsItems = computed(() => {
  return [
    { title: '上课记录', icon: 'user-o', action: 'classRecord', role: 'teacher' },
    { title: '配置签名', icon: 'edit', action: 'configSignature' },
    { title: '我的推荐', icon: 'gift-o', action: 'referral' },
  ].filter(item => !item.role || hasRole(item.role))
})

onMounted(async () => {
  await ensureWecomLogin(route.query.code)
  await initData()
})

// 从独立签名页保存签名后返回：keep-alive 下 onMounted 不会重跑，这里检测标记刷新用户信息
onActivated(() => {
  if (sessionStorage.getItem('signatureSaved') === '1') {
    sessionStorage.removeItem('signatureSaved')
    initData()
  }
})

async function initData() {
  try {
    const userInfoResponse = await getUserInfo()
    userInfo.value = userInfoResponse
    setUserInfo(userInfoResponse)
    initializeCompanyContext(userInfoResponse)
    getClassHourStatics();
  } catch (error) {
    console.error('初始化数据失败:', error)
  } finally {
    loading.value = false
  }
}

function getClassHourStatics() {
  getTeacherClassHourStatics().then(response => {
    if (response) {
      classHourStatics.value = response
    }
  })
}

/** 跳转课时构成详情（累计/本月/本周） */
function goToComposition(timeType) {
  router.push({
    path: '/classHourComposition',
    query: { timeType }
  })
}

function handleSettingClick(item) {
  if (item.action === 'classRecord') {
    router.push({ path: '/myTeachingLog' })
  } else if (item.action === 'configSignature') {
    // 跳转独立签名页（整页全屏），保存成功后返回本页刷新
    router.push({
      path: '/signature',
      query: { from: 'mine', initialImage: userInfo.value?.user?.signImageUrl || '' }
    })
  } else if (item.action === 'referral') {
    router.push({ path: '/referral/mine' })
  } else {
    console.log('点击设置项:', item.title)
  }
}
</script>

<style scoped>
.mine-page {
  background-color: #f5f7fa;
  min-height: 100%;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 300px;
  background-color: #f5f7fa;
}

.loading-text {
  margin-top: 12px;
  color: #999;
  font-size: 14px;
}

.page-content {
  padding: 16px;
  padding-bottom: calc(60px + env(safe-area-inset-bottom));
}

/* 个人信息卡片 */
.profile-card {
  background: #fff;
  border-radius: 16px;
  padding: 24px 20px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.profile-header {
  display: flex;
  align-items: center;
  gap: 16px;
}

.avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(22, 119, 255, 0.25);
  overflow: hidden;
}

.avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-info {
  flex: 1;
  min-width: 0;
}

.name-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.name {
  font-size: 20px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.profile-meta {
  display: flex;
  align-items: center;
  gap: 12px;
}

.campus {
  font-size: 14px;
  color: #999;
}

.role {
  font-size: 12px;
  color: #1677ff;
  background: rgba(22, 119, 255, 0.1);
  padding: 2px 8px;
  border-radius: 4px;
}

/* 统计区域 */
.stats-section {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

.stat-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px 8px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.stat-card:active {
  opacity: 0.7;
}

.stat-value {
  font-size: 22px;
  font-weight: 600;
  color: #1677ff;
  line-height: 1;
  margin-bottom: 8px;
}

.stat-label {
  font-size: 12px;
  color: #666;
}

/* 设置列表 */
.settings-section {
  margin-top: 8px;
}

:deep(.van-cell-group--inset) {
  margin: 0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

:deep(.van-cell) {
  padding: 14px 16px;
  transition: background-color 0.2s ease;
}

:deep(.van-cell:active) {
  background-color: #f5f5f5;
}

:deep(.van-cell__title) {
  font-size: 15px;
  color: #262626;
}

:deep(.van-cell[danger]) .van-cell__title {
  color: #ff4d4f;
}
</style>
